import { FC, useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button, Checkbox, Col, Collapse, DatePicker, Form, Input, Row, Select } from "antd";
import { FormLayout, useForm } from "antd/lib/form/Form";

import styles from "../OutletManagementPage/OutletManagementPage.module.css";

import { Head } from "components/molecules/Head/Head";
import Label from "components/atoms/Label/Label";
import PageTitle from "components/molecules/PageTitle/PageTitle";
import { PageTitles, Routes as ROUTES } from "constants/global";
import Outlet from "../types/Outlet";
import { AIR_LAND_OPTIONS, DEFAULT_DATA, FIELD_CONFIG, FORM_CONFIG, TERMINAL_OPTIONS } from "../config";
import { useAppSelector } from "app/hooks/hooks";
import { createOutletSelector } from "redux/userList/userSlice";
import Spinner from "components/atoms/Spinner/Spinner";
import CountDownModal from "components/molecules/ModalContainer/CountDownModalContainer";
import useCreateOrUpdateOutlet from "../hooks/useCreateOrUpdateOutlet";
import Tag from "components/atoms/Tag/Tag";
import CustomTagProps from "components/atoms/Tag/types/CustomTagProps";
import useGetFormOptions from "../hooks/useGetFormOptions";

const { validateMessages, layout, validateTrigger } = FORM_CONFIG;

const CreateOutletPage: FC = () => {
	const [form] = useForm();
	const { tags, categoryType3Tags } = useGetFormOptions();
	const { createOrUpdateOutlet } = useCreateOrUpdateOutlet();
	const navigate = useNavigate();
	const [isFormValid, setIsFormValid] = useState(false);
	const { loading: isLoading } = useAppSelector(createOutletSelector);

	useEffect(() => checkFormValidity(), [form]);

	const onFinish = async (values: { outlets: Outlet[] }) => {
		try {
			await createOrUpdateOutlet(values.outlets);
			CountDownModal({
				timer: 3,
				title: values.outlets.length > 1 ? "Outlets created successfully!" : "Outlet created successfully!",
				onTimeComplete: () => navigate(ROUTES.OUTLET_MANAGEMENT)
			});
		} catch (e) {
			CountDownModal({
				timer: 3,
				errorIcon: true,
				title: "Unable to proceed."
			});
		}
	};

	const checkFormValidity = () => {
		const formValues: { outlets: Outlet[] } = form.getFieldsValue();
		const hasErrors = form.getFieldsError().some(({ errors }) => errors.length);
		const requiredFields = Object.keys(FIELD_CONFIG).filter((key) => {
			if (key === "vendorCode") return false;
			return FIELD_CONFIG[key].required;
		});
		const allRequiredFieldsFilled = formValues.outlets.every((outlet: Outlet) => {
			// Check all required fields except vendorCode
			const baseFieldsValid = requiredFields.every((field) => outlet[field as keyof Outlet] !== "");

			// Add vendorCode validation only if participatingInChangiEv is true
			if (outlet.participatingInChangiEv) {
				return baseFieldsValid && outlet.vendorCode !== "";
			}

			return baseFieldsValid;
		});

		setIsFormValid(!hasErrors && allRequiredFieldsFilled && formValues.outlets.length > 0);
	};

	const getVendorCodeRules = (fieldName: number) => [
		{
			required: form.getFieldValue(["outlets", fieldName, "participatingInChangiEv"]),
			message: ""
		}
	];

	const handleValuesChange = () => checkFormValidity();

	const tagRender = (props: CustomTagProps) => {
		const { label, onClose } = props;
		return <Tag onClose={onClose}>{label}</Tag>;
	};

	const validateUniqueOutletCode = (value: string, callback: (message?: string) => void, allValues: Outlet[]) => {
		const outletCodes = allValues.map((item) => item.outletCode);
		const occurrences = outletCodes.filter((code) => code === value).length;

		if (occurrences > 1) {
			callback("Outlet code must be unique.");
		} else {
			callback();
		}
	};

	return (
		<>
			<Head>{PageTitles.CREATE_OUTLET}</Head>
			<div className={styles.root}>
				{isLoading === 1 && <Spinner type="transparent" />}
				<div className="mb-8">
					<PageTitle
						showBackIcon
						goBack={() => navigate(ROUTES.OUTLET_MANAGEMENT)}
						title="Create Outlet"
						subTitle="Fill in the details on this form to create up to 10 outlets at once"
					/>
				</div>

				<div className="p-9">
					<Form
						form={form}
						requiredMark={false}
						validateTrigger={validateTrigger}
						validateMessages={validateMessages}
						layout={layout as FormLayout}
						initialValues={{ outlets: [{ ...DEFAULT_DATA }] }}
						onValuesChange={handleValuesChange}
						onFinish={onFinish}
					>
						<Form.List name="outlets">
							{(fields, { add, remove }) => (
								<div className="flex flex-col gap-4">
									{fields.map((field) => (
										<Collapse key={field.key} className="bg-transparent" defaultActiveKey={fields.map(({ key: fieldKey }) => fieldKey)}>
											<Collapse.Panel key={field.key} header={<span className="font-bold text-lg">Outlet #{field.name + 1}</span>}>
												<div className="flex justify-end font-bold mb-5">
													<span className="cursor-pointer" onClick={() => remove(field.name)}>
														Remove outlet
													</span>
												</div>
												<div>
													<Row gutter={16}>
														<Col className="gutter-row" sm={12} md={6}>
															<Form.Item rules={[{ required: FIELD_CONFIG["outletStatus"].required }]} name={[field.name, "outletStatus"]} valuePropName="checked">
																<Checkbox className={styles["custom-checkbox"]}>
																	<span>{FIELD_CONFIG["outletStatus"].label}</span>
																</Checkbox>
															</Form.Item>
														</Col>
														<Col className="gutter-row" sm={12} md={8}>
															<Form.Item
																rules={[{ required: FIELD_CONFIG["participatingInChangiEv"].required }]}
																name={[field.name, "participatingInChangiEv"]}
																valuePropName="checked"
															>
																<Checkbox
																	className={styles["custom-checkbox"]}
																	onChange={() => {
																		// Force re-render of vendorCode field
																		form.validateFields([["outlets", field.name, "vendorCode"]]);
																		checkFormValidity();
																	}}
																>
																	<span>{FIELD_CONFIG["participatingInChangiEv"].label}</span>
																</Checkbox>
															</Form.Item>
														</Col>
													</Row>

													<Row gutter={16}>
														<Col className="gutter-row" xs={24} md={8}>
															<Form.Item
																rules={[{ required: FIELD_CONFIG["tenantCode"].required }]}
																label={
																	<Label requiredMark={FIELD_CONFIG["tenantCode"].required} label={FIELD_CONFIG["tenantCode"].label} classes={{ label: styles?.["form-label"] }} />
																}
																name={[field.name, "tenantCode"]}
															>
																<Input className={styles["cag-input"]} placeholder={FIELD_CONFIG["tenantCode"].label} maxLength={20} />
															</Form.Item>
														</Col>
														<Col className="gutter-row" xs={24} md={12}>
															<Form.Item
																rules={[{ required: FIELD_CONFIG["tenantName"].required }]}
																label={
																	<Label requiredMark={FIELD_CONFIG["tenantName"].required} label={FIELD_CONFIG["tenantName"].label} classes={{ label: styles?.["form-label"] }} />
																}
																name={[field.name, "tenantName"]}
															>
																<Input className={styles["cag-input"]} placeholder={FIELD_CONFIG["tenantName"].label} maxLength={100} />
															</Form.Item>
														</Col>
													</Row>

													<Row gutter={16}>
														<Col className="gutter-row" xs={24} md={8}>
															<Form.Item
																rules={[
																	{ required: FIELD_CONFIG["outletCode"].required },
																	({ getFieldsValue }) => ({
																		validator(_, value): Promise<void> {
																			const formValues: { outlets: Outlet[] } = getFieldsValue();
																			return new Promise((resolve, reject) => {
																				validateUniqueOutletCode(
																					value,
																					(message) => {
																						message ? reject(message) : resolve();
																					},
																					formValues.outlets
																				);
																			});
																		}
																	})
																]}
																label={
																	<Label requiredMark={FIELD_CONFIG["outletCode"].required} label={FIELD_CONFIG["outletCode"].label} classes={{ label: styles?.["form-label"] }} />
																}
																name={[field.name, "outletCode"]}
																normalize={(value: string) => value.trim().toUpperCase()}
															>
																<Input className={styles["cag-input"]} placeholder={FIELD_CONFIG["outletCode"].label} maxLength={20} />
															</Form.Item>
														</Col>
														<Col className="gutter-row" xs={24} md={12}>
															<Form.Item
																rules={[{ required: FIELD_CONFIG["outletName"].required }]}
																label={
																	<Label requiredMark={FIELD_CONFIG["outletName"].required} label={FIELD_CONFIG["outletName"].label} classes={{ label: styles?.["form-label"] }} />
																}
																name={[field.name, "outletName"]}
															>
																<Input className={styles["cag-input"]} placeholder={FIELD_CONFIG["outletName"].label} maxLength={100} />
															</Form.Item>
														</Col>
													</Row>

													<Row gutter={16}>
														<Col className="gutter-row" xs={24} md={8}>
															{/* Forcing of input checks upon changi EV checkbox change */}
															<Form.Item dependencies={[["outlets", field.name, "participatingInChangiEv"]]} noStyle>
																{({ getFieldValue }) => {
																	const isRequired = getFieldValue(["outlets", field.name, "participatingInChangiEv"]);
																	return (
																		<Form.Item
																			rules={getVendorCodeRules(field.name)}
																			label={<Label requiredMark={isRequired} label={FIELD_CONFIG["vendorCode"].label} classes={{ label: styles?.["form-label"] }} />}
																			name={[field.name, "vendorCode"]}
																			validateTrigger={["onChange", "onBlur"]}
																		>
																			<Input className={styles["cag-input"]} placeholder={FIELD_CONFIG["vendorCode"].label} maxLength={10} />
																		</Form.Item>
																	);
																}}
															</Form.Item>
														</Col>
													</Row>

													<Row gutter={16}>
														<Col className="gutter-row" xs={24} md={8}>
															<Form.Item
																rules={[{ required: FIELD_CONFIG["rentalSerialNo"].required }]}
																label={
																	<Label
																		requiredMark={FIELD_CONFIG["rentalSerialNo"].required}
																		label={FIELD_CONFIG["rentalSerialNo"].label}
																		classes={{ label: styles?.["form-label"] }}
																	/>
																}
																name={[field.name, "rentalSerialNo"]}
															>
																<Input className={styles["cag-input"]} placeholder={FIELD_CONFIG["rentalSerialNo"].label} maxLength={20} />
															</Form.Item>
														</Col>
													</Row>

													<Row gutter={16}>
														<Col className="gutter-row" xs={24} md={8}>
															<Form.Item
																rules={[{ required: FIELD_CONFIG["effectiveDate"].required }]}
																label={
																	<Label
																		requiredMark={FIELD_CONFIG["effectiveDate"].required}
																		label={FIELD_CONFIG["effectiveDate"].label}
																		classes={{ label: styles?.["form-label"] }}
																	/>
																}
																name={[field.name, "effectiveDate"]}
															>
																<DatePicker placeholder={FIELD_CONFIG["effectiveDate"].label} className={styles["custom-datepicker"]} />
															</Form.Item>
														</Col>

														<Col className="gutter-row" xs={24} md={8}>
															<Form.Item
																rules={[{ required: FIELD_CONFIG["expiryDate"].required }]}
																label={
																	<Label requiredMark={FIELD_CONFIG["expiryDate"].required} label={FIELD_CONFIG["expiryDate"].label} classes={{ label: styles?.["form-label"] }} />
																}
																name={[field.name, "expiryDate"]}
															>
																<DatePicker placeholder={FIELD_CONFIG["expiryDate"].label} className={styles["custom-datepicker"]} />
															</Form.Item>
														</Col>
													</Row>

													<Row gutter={16}>
														<Col className="gutter-row" xs={24} md={4}>
															<Form.Item
																rules={[{ required: FIELD_CONFIG["terminal"].required }]}
																label={
																	<Label requiredMark={FIELD_CONFIG["terminal"].required} label={FIELD_CONFIG["terminal"].label} classes={{ label: styles?.["form-label"] }} />
																}
																name={[field.name, "terminal"]}
															>
																<Select popupClassName="custom-dropdown" placeholder={FIELD_CONFIG["terminal"].label} options={TERMINAL_OPTIONS} />
															</Form.Item>
														</Col>

														<Col className="gutter-row" xs={24} md={4}>
															<Form.Item
																rules={[{ required: FIELD_CONFIG["unitNo"].required }]}
																label={<Label requiredMark={FIELD_CONFIG["unitNo"].required} label={FIELD_CONFIG["unitNo"].label} classes={{ label: styles?.["form-label"] }} />}
																name={[field.name, "unitNo"]}
															>
																<Input className={styles["cag-input"]} placeholder={FIELD_CONFIG["unitNo"].label} maxLength={100} />
															</Form.Item>
														</Col>

														<Col className="gutter-row" xs={24} md={6}>
															<Form.Item
																rules={[{ required: FIELD_CONFIG["location"].required }]}
																label={
																	<Label requiredMark={FIELD_CONFIG["location"].required} label={FIELD_CONFIG["location"].label} classes={{ label: styles?.["form-label"] }} />
																}
																name={[field.name, "location"]}
															>
																<Input className={styles["cag-input"]} placeholder={FIELD_CONFIG["location"].label} maxLength={100} />
															</Form.Item>
														</Col>

														<Col className="gutter-row" xs={24} md={4}>
															<Form.Item
																rules={[{ required: FIELD_CONFIG["airLand"].required }]}
																label={<Label requiredMark={FIELD_CONFIG["airLand"].required} label={FIELD_CONFIG["airLand"].label} classes={{ label: styles?.["form-label"] }} />}
																name={[field.name, "airLand"]}
															>
																<Select popupClassName="custom-dropdown" placeholder={FIELD_CONFIG["airLand"].label} options={AIR_LAND_OPTIONS} />
															</Form.Item>
														</Col>
													</Row>

													<Row gutter={16}>
														<Col className="gutter-row" xs={24} md={8}>
															<Form.Item
																rules={[{ required: FIELD_CONFIG["concession"].required }]}
																label={
																	<Label requiredMark={FIELD_CONFIG["concession"].required} label={FIELD_CONFIG["concession"].label} classes={{ label: styles?.["form-label"] }} />
																}
																name={[field.name, "concession"]}
															>
																<Input className={styles["cag-input"]} placeholder={FIELD_CONFIG["concession"].label} maxLength={100} />
															</Form.Item>
														</Col>
													</Row>

													<Row gutter={16}>
														<Col className="gutter-row" xs={24} md={8}>
															<Form.Item
																rules={[{ required: FIELD_CONFIG["trade"].required }]}
																label={<Label requiredMark={FIELD_CONFIG["trade"].required} label={FIELD_CONFIG["trade"].label} classes={{ label: styles?.["form-label"] }} />}
																name={[field.name, "trade"]}
															>
																<Input className={styles["cag-input"]} placeholder={FIELD_CONFIG["trade"].label} maxLength={100} />
															</Form.Item>
														</Col>
													</Row>

													<Row gutter={16}>
														<Col className="gutter-row" xs={24} md={8}>
															<Form.Item
																rules={[{ required: FIELD_CONFIG["businessCategory"].required }]}
																label={
																	<Label
																		requiredMark={FIELD_CONFIG["businessCategory"].required}
																		label={FIELD_CONFIG["businessCategory"].label}
																		classes={{ label: styles?.["form-label"] }}
																	/>
																}
																name={[field.name, "businessCategory"]}
															>
																<Input className={styles["cag-input"]} placeholder={FIELD_CONFIG["businessCategory"].label} maxLength={100} />
															</Form.Item>
														</Col>
													</Row>

													<Row gutter={16}>
														<Col className="gutter-row" xs={24} md={8}>
															<Form.Item
																rules={[{ required: FIELD_CONFIG["productCategory"].required }]}
																label={
																	<Label
																		requiredMark={FIELD_CONFIG["productCategory"].required}
																		label={FIELD_CONFIG["productCategory"].label}
																		classes={{ label: styles?.["form-label"] }}
																	/>
																}
																name={[field.name, "productCategory"]}
															>
																<Input className={styles["cag-input"]} placeholder={FIELD_CONFIG["productCategory"].label} maxLength={100} />
															</Form.Item>
														</Col>
													</Row>

													<Row gutter={16}>
														<Col className="gutter-row" xs={24} md={8}>
															<Form.Item
																rules={[{ required: FIELD_CONFIG["floorName"].required }]}
																label={
																	<Label requiredMark={FIELD_CONFIG["floorName"].required} label={FIELD_CONFIG["floorName"].label} classes={{ label: styles?.["form-label"] }} />
																}
																name={[field.name, "floorName"]}
															>
																<Input className={styles["cag-input"]} placeholder={FIELD_CONFIG["floorName"].label} maxLength={50} />
															</Form.Item>
														</Col>
													</Row>

													<Row gutter={16}>
														<Col className="gutter-row" xs={24} md={8}>
															<Form.Item
																rules={[{ required: FIELD_CONFIG["categoryType1"].required }]}
																label={
																	<Label
																		requiredMark={FIELD_CONFIG["categoryType1"].required}
																		label={FIELD_CONFIG["categoryType1"].label}
																		classes={{ label: styles?.["form-label"] }}
																	/>
																}
																name={[field.name, "categoryType1"]}
															>
																<Input className={styles["cag-input"]} placeholder={FIELD_CONFIG["categoryType1"].label} maxLength={100} />
															</Form.Item>
														</Col>
														<Col className="gutter-row" xs={24} md={8}>
															<Form.Item
																rules={[{ required: FIELD_CONFIG["categoryType2"].required }]}
																label={
																	<Label
																		requiredMark={FIELD_CONFIG["categoryType2"].required}
																		label={FIELD_CONFIG["categoryType2"].label}
																		classes={{ label: styles?.["form-label"] }}
																	/>
																}
																name={[field.name, "categoryType2"]}
															>
																<Input className={styles["cag-input"]} placeholder={FIELD_CONFIG["categoryType2"].label} maxLength={100} />
															</Form.Item>
														</Col>
													</Row>

													<Row gutter={16}>
														<Col className="gutter-row" xs={24} md={8}>
															<Form.Item
																rules={[{ required: FIELD_CONFIG["categoryType3"].required }]}
																label={
																	<Label
																		requiredMark={FIELD_CONFIG["categoryType3"].required}
																		label={FIELD_CONFIG["categoryType3"].label}
																		classes={{ label: styles?.["form-label"] }}
																	/>
																}
																name={[field.name, "categoryType3"]}
															>
																<Select
																	showArrow
																	mode="multiple"
																	popupClassName="custom-dropdown"
																	placeholder={FIELD_CONFIG["categoryType3"].label}
																	options={categoryType3Tags.map((tag) => ({ value: tag.name }))}
																/>
															</Form.Item>
														</Col>
													</Row>

													<Row gutter={16}>
														<Col className="gutter-row" xs={24} md={8}>
															<Form.Item
																rules={[{ required: FIELD_CONFIG["categoryType4"].required }]}
																label={
																	<Label
																		requiredMark={FIELD_CONFIG["categoryType4"].required}
																		label={FIELD_CONFIG["categoryType4"].label}
																		classes={{ label: styles?.["form-label"] }}
																	/>
																}
																name={[field.name, "categoryType4"]}
															>
																<Input className={styles["cag-input"]} placeholder={FIELD_CONFIG["categoryType4"].label} maxLength={100} />
															</Form.Item>
														</Col>
													</Row>

													<Row gutter={16}>
														<Col className="gutter-row" xs={24} md={8}>
															<Form.Item
																rules={[{ required: FIELD_CONFIG["categoryType5"].required }]}
																label={
																	<Label
																		requiredMark={FIELD_CONFIG["categoryType5"].required}
																		label={FIELD_CONFIG["categoryType5"].label}
																		classes={{ label: styles?.["form-label"] }}
																	/>
																}
																name={[field.name, "categoryType5"]}
															>
																<Input className={styles["cag-input"]} placeholder={FIELD_CONFIG["categoryType5"].label} maxLength={100} />
															</Form.Item>
														</Col>
													</Row>

													<Row gutter={16}>
														<Col className="gutter-row" xs={24} md={12}>
															<Form.Item
																rules={[{ required: FIELD_CONFIG["tags"].required }]}
																label={<Label requiredMark={FIELD_CONFIG["tags"].required} label={FIELD_CONFIG["tags"].label} classes={{ label: styles?.["form-label"] }} />}
																name={[field.name, "tags"]}
															>
																<Select
																	mode="multiple"
																	showArrow
																	popupClassName="custom-dropdown"
																	tagRender={tagRender}
																	placeholder={FIELD_CONFIG["tags"].label}
																	options={tags.map((tag) => ({
																		value: tag.tag_name
																	}))}
																/>
															</Form.Item>
														</Col>
													</Row>
												</div>
											</Collapse.Panel>
										</Collapse>
									))}
									{fields.length < 10 && (
										<Button type="dashed" onClick={() => add({ ...DEFAULT_DATA })} className="text-base block h-auto p-3 rounded-lg border-grey-200">
											+ Add outlet
										</Button>
									)}
								</div>
							)}
						</Form.List>

						<div className="mt-8 flex justify-between items-center">
							<Link className="font-bold" to={ROUTES.OUTLET_MANAGEMENT}>
								Go back
							</Link>
							<Form.Item shouldUpdate>
								{() => (
									<Button className={styles?.["btn-primary"]} htmlType="submit" disabled={!form.isFieldsTouched() || !isFormValid} aria-label="submit">
										Create Outlet
									</Button>
								)}
							</Form.Item>
						</div>

						{window.location.hostname === "localhost" && (
							<Form.Item shouldUpdate>
								{() => (
									<>
										<div className="mt-9 p-5 bg-grey-25">
											<p className="font-bold mb-5">Form errors</p>
											<pre>
												{JSON.stringify(
													form.getFieldsError().filter((field) => field.errors.length > 0),
													null,
													2
												)}
											</pre>
										</div>
										<div className="mt-9 p-5 bg-grey-25">
											<p className="font-bold mb-5">Form output</p>
											<pre>{JSON.stringify(form.getFieldsValue(), null, 2)}</pre>
										</div>
									</>
								)}
							</Form.Item>
						)}
					</Form>
				</div>
			</div>
		</>
	);
};

export default CreateOutletPage;
